// Boundary edge / vertex extraction from tessellated BRep face ranges.
// Used for Inventor-style measure snapping (vertex > edge > face).
import * as THREE from 'three';
import { isPointClipped } from './geometry';
import type { OcctBrepFace } from './types';
import { camera } from './viewer';

export interface BoundaryEdge {
  a: THREE.Vector3;
  b: THREE.Vector3;
  ia: number;
  ib: number;
}

export interface FaceBoundary {
  edges: BoundaryEdge[];
  vertices: THREE.Vector3[];
}

const cache = new Map<string, FaceBoundary>();

function edgeKey(i: number, j: number): string {
  return i < j ? i + ':' + j : j + ':' + i;
}

/** Unique boundary edges (count === 1) for a BRep face triangle range. */
export function getFaceBoundary(mesh: THREE.Mesh, range: { first: number; last: number }): FaceBoundary {
  const key = mesh.uuid + ':' + range.first + ':' + range.last;
  const hit = cache.get(key);
  if (hit) return hit;

  const geometry = mesh.geometry;
  const posAttr = geometry.getAttribute('position') as THREE.BufferAttribute;
  const index = geometry.index;
  if (!posAttr || !index) {
    const empty = { edges: [], vertices: [] };
    cache.set(key, empty);
    return empty;
  }
  const idx = index.array;
  // Mesh formats duplicate vertices per triangle; weld ids (from mesh-faces)
  // make shared edges count as shared. STEP meshes are already shared.
  const weld = geometry.userData.weldIds as Uint32Array | undefined;
  const wid = (i: number): number => (weld ? weld[i] : i);
  const counts = new Map<string, { ia: number; ib: number; n: number }>();

  for (let t = range.first; t <= range.last; t++) {
    const i0 = idx[t * 3], i1 = idx[t * 3 + 1], i2 = idx[t * 3 + 2];
    for (const [a, b] of [[i0, i1], [i1, i2], [i2, i0]] as const) {
      const k = edgeKey(wid(a), wid(b));
      const cur = counts.get(k);
      if (cur) cur.n++;
      else counts.set(k, { ia: a, ib: b, n: 1 });
    }
  }

  const edges: BoundaryEdge[] = [];
  const vertIdx = new Map<number, number>();
  counts.forEach(e => {
    if (e.n !== 1) return;
    const a = new THREE.Vector3().fromBufferAttribute(posAttr, e.ia).applyMatrix4(mesh.matrixWorld);
    const b = new THREE.Vector3().fromBufferAttribute(posAttr, e.ib).applyMatrix4(mesh.matrixWorld);
    edges.push({ a, b, ia: e.ia, ib: e.ib });
    vertIdx.set(wid(e.ia), e.ia);
    vertIdx.set(wid(e.ib), e.ib);
  });

  const vertices = Array.from(vertIdx.values()).map(i =>
    new THREE.Vector3().fromBufferAttribute(posAttr, i).applyMatrix4(mesh.matrixWorld)
  );

  const result = { edges, vertices };
  cache.set(key, result);
  return result;
}

export function clearTopologyCache(): void {
  cache.clear();
}

function toScreen(p: THREE.Vector3, width: number, height: number): THREE.Vector2 {
  const v = p.clone().project(camera);
  return new THREE.Vector2((v.x * 0.5 + 0.5) * width, (-v.y * 0.5 + 0.5) * height);
}

function distPointSegment2D(p: THREE.Vector2, a: THREE.Vector2, b: THREE.Vector2): { d: number; t: number } {
  const ab = b.clone().sub(a);
  const len2 = ab.lengthSq();
  if (len2 < 1e-12) return { d: p.distanceTo(a), t: 0 };
  const t = Math.max(0, Math.min(1, p.clone().sub(a).dot(ab) / len2));
  const proj = a.clone().addScaledVector(ab, t);
  return { d: p.distanceTo(proj), t };
}

export type SnapKind = 'vertex' | 'edge' | 'none';

export interface TopologySnap {
  kind: SnapKind;
  point: THREE.Vector3;
  /** For edges: endpoints */
  edgeA?: THREE.Vector3;
  edgeB?: THREE.Vector3;
}

const VERTEX_PX = 14;
const EDGE_PX = 10;

/**
 * After a face hit, prefer a nearby boundary vertex, then edge, else none
 * (keep the face pick). Thresholds are in screen pixels.
 *
 * A face can run on past the section plane - the wall you are measuring
 * carries on up into the storey that was cut away - so candidates the section
 * removed are skipped. An edge that crosses the plane keeps the part you can
 * still see: the snap point itself is what gets tested.
 */
export function snapTopology(
  mesh: THREE.Mesh,
  brepFace: OcctBrepFace | null,
  triangleIndex: number,
  clientX: number,
  clientY: number,
  canvasRect: DOMRect,
  clippingPlanes: readonly THREE.Plane[] = []
): TopologySnap | null {
  const range = brepFace || { first: triangleIndex, last: triangleIndex };
  const boundary = getFaceBoundary(mesh, range);
  if (boundary.edges.length === 0 && boundary.vertices.length === 0) return null;

  const w = canvasRect.width;
  const h = canvasRect.height;
  const cursor = new THREE.Vector2(clientX - canvasRect.left, clientY - canvasRect.top);

  let bestVert: THREE.Vector3 | null = null;
  let bestVertD = VERTEX_PX;
  for (const v of boundary.vertices) {
    if (isPointClipped(v, clippingPlanes)) continue;
    const s = toScreen(v, w, h);
    const d = cursor.distanceTo(s);
    if (d < bestVertD) { bestVertD = d; bestVert = v; }
  }
  if (bestVert) {
    return { kind: 'vertex', point: bestVert.clone() };
  }

  let bestEdge: BoundaryEdge | null = null;
  let bestT = 0;
  let bestEdgeD = EDGE_PX;
  for (const e of boundary.edges) {
    const sa = toScreen(e.a, w, h);
    const sb = toScreen(e.b, w, h);
    const { d, t } = distPointSegment2D(cursor, sa, sb);
    if (d >= bestEdgeD) continue;
    if (isPointClipped(e.a.clone().lerp(e.b, t), clippingPlanes)) continue;
    bestEdgeD = d; bestEdge = e; bestT = t;
  }
  if (bestEdge) {
    const point = bestEdge.a.clone().lerp(bestEdge.b, bestT);
    return {
      kind: 'edge',
      point,
      edgeA: bestEdge.a.clone(),
      edgeB: bestEdge.b.clone()
    };
  }

  return null;
}

export { closestPointsOnSegments } from './geometry';
